import React, { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getToken } from '../services/api';
import CopyBtn from './CopyBtn';
import './TokenModal.css';

function TokenModal({ tokenId, onClose }) {
  const [copied, setCopied] = useState(false);

  const { data: token, isLoading } = useQuery({
    queryKey: ['token', tokenId],
    queryFn: () => getToken(tokenId),
  });

  // Gestion de la touche Echap
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  // Fonction pour copier l'adresse
  const copyAddress = async () => {
    if (!token) return;
    
    try {
      await navigator.clipboard.writeText(token.address);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Erreur copie:', err);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const calculateTokenCreationDate = (detectionDate, ageMinutes) => {
    if (!detectionDate || !ageMinutes) return null;
    const detection = new Date(detectionDate);
    const creation = new Date(detection.getTime() - (ageMinutes * 60 * 1000));
    return creation.toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const getDexScreenerUrl = () => {
    if (!token) return '#';
    return `https://dexscreener.com/${token.chain}/${token.ui_mint || token.address}`;
  };

  const formatSource = (src) => {
    if (!src) return 'N/A';
    const s = String(src).toLowerCase();
    if (s.includes('solana')) return 'Solana';
    if (s.includes('boost') || s.includes('dex')) return 'Boosted';
    if (s.includes('evm') || s.includes('quicknode') || s.includes('bsc') || s.includes('eth') || s.includes('base') || s.includes('arbitrum')) return 'EVM';
    return src;
  };

  const getDetectionRequest = () => {
    const md = token?.token_metadata || {};
    const req = md?.raw_evaluation_details?.detection_request || md?.raw_token_data?.request || md?.detection_request;
    if (!req) return null;
    if (typeof req === 'string') return req;
    if (typeof req === 'object' && req.url) return req.url;
    return String(req);
  };

  const getDetectionParams = () => {
    const md = token?.token_metadata || {};
    
    // Chercher les paramètres dans plusieurs emplacements possibles
    const req = md?.raw_evaluation_details?.detection_request || md?.raw_token_data?.request || md?.detection_request;
    const paramsDirect = md?.raw_evaluation_details?.detection_params || md?.raw_token_data?.params || md?.detection_params || md?.params;
    const queryParams = md?.raw_evaluation_details?.query_params || md?.raw_token_data?.query_params || md?.query_params;
    
    // Priorité 1: Paramètres directs
    if (paramsDirect && typeof paramsDirect === 'object' && Object.keys(paramsDirect).length > 0) {
      return paramsDirect;
    }
    
    // Priorité 2: Query params directs
    if (queryParams && typeof queryParams === 'object' && Object.keys(queryParams).length > 0) {
      return queryParams;
    }
    
    // Priorité 3: Extraire depuis l'objet request
    if (req && typeof req === 'object') {
      // Extraire les paramètres (exclure 'url' qui est déjà affiché)
      const params = { ...req };
      delete params.url;
      
      // Si des paramètres existent, les retourner
      if (Object.keys(params).length > 0) {
        return params;
      }
    }
    
    // Priorité 4: Si c'est une string URL, essayer d'extraire les query params
    if (req && typeof req === 'string') {
      try {
        const url = new URL(req);
        const params = {};
        url.searchParams.forEach((value, key) => {
          params[key] = value;
        });
        if (Object.keys(params).length > 0) {
          return params;
        }
      } catch {
        // Pas une URL valide, ignorer
      }
    }
    
    return null;
  };

  const formatDetectionParams = (params) => {
    if (!params || typeof params !== 'object') return null;
    
    // Masquer les paramètres sensibles
    const sensitive = ['api_key', 'apikey', 'api-key', 'key', 'token', 'access_token', 'access-token', 'authorization'];
    const formatted = {};
    
    Object.keys(params).forEach(key => {
      const value = params[key];
      const isSensitive = sensitive.some(s => key.toLowerCase().includes(s.toLowerCase()));
      formatted[key] = isSensitive ? '***' : value;
    });
    
    return formatted;
  };

  const formatDetectionParamsAsJson = (params) => {
    const formatted = formatDetectionParams(params);
    if (!formatted) return '';
    
    try {
      // Formater en JSON lisible avec indentation
      return JSON.stringify(formatted, null, 2);
    } catch {
      // Si erreur, retourner une représentation simple
      return JSON.stringify(formatted);
    }
  };

  const redactUrl = (u) => {
    if (!u || typeof u !== 'string') return u;
    try {
      const sensitive = ['api_key', 'apikey', 'api-key', 'key', 'token', 'access_token', 'access-token'];
      let s = u;
      sensitive.forEach((param) => {
        const re = new RegExp(`${param}=([^&]+)`, 'gi');
        s = s.replace(re, `${param}=***`);
      });
      return s;
    } catch {
      return u;
    }
  };

  if (isLoading) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="modal-loading">
            <div className="spinner"></div>
            <p>Chargement...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!token) return null;

  const metadata = token.token_metadata || {};
  
  // Déclarer d'abord toutes les sources de données
  const socialPresence = metadata.social_presence || {};
  const marketData = metadata.market_data || {};
  const moralisDiscovery = metadata.moralis_discovery || {};
  
  // 🔄 Rétrocompatibilité: goplus peut être dans metadata.goplus_security (nouveau) ou rawDetails.goplus_security (ancien)
  const goplusNew = metadata.goplus_security || {};
  const goplusOld = (metadata.raw_evaluation_details || {}).goplus_security || {};
  const goplusRaw = Object.keys(goplusNew).length > 0 ? goplusNew : goplusOld;
  
  // 🛠️ Helper: Convertir les champs GoPlus de string '0'/'1' en booléens
  const goplusBool = (value) => {
    if (value === null || value === undefined) return false;
    if (typeof value === 'string') return value === '1' || value === 'true';
    return Boolean(value);
  };
  
  // Normaliser les champs booléens de GoPlus
  const goplus = {
    ...goplusRaw,
    is_honeypot: goplusBool(goplusRaw.is_honeypot),
    is_mintable: goplusBool(goplusRaw.is_mintable),
    is_open_source: goplusBool(goplusRaw.is_open_source),
    cannot_buy: goplusBool(goplusRaw.cannot_buy),
    cannot_sell_all: goplusBool(goplusRaw.cannot_sell_all),
    is_proxy: goplusBool(goplusRaw.is_proxy),
    is_anti_whale: goplusBool(goplusRaw.is_anti_whale),
    is_blacklisted: goplusBool(goplusRaw.is_blacklisted),
    is_whitelisted: goplusBool(goplusRaw.is_whitelisted),
    transfer_pausable: goplusBool(goplusRaw.transfer_pausable),
    trading_cooldown: goplusBool(goplusRaw.trading_cooldown),
    hidden_owner: goplusBool(goplusRaw.hidden_owner),
    selfdestruct: goplusBool(goplusRaw.selfdestruct),
    owner_change_balance: goplusBool(goplusRaw.owner_change_balance),
    can_take_back_ownership: goplusBool(goplusRaw.can_take_back_ownership),
    slippage_modifiable: goplusBool(goplusRaw.slippage_modifiable),
    personal_slippage_modifiable: goplusBool(goplusRaw.personal_slippage_modifiable),
    anti_whale_modifiable: goplusBool(goplusRaw.anti_whale_modifiable),
    external_call: goplusBool(goplusRaw.external_call),
  };

  console.log('moralisDiscovery', moralisDiscovery);
  console.log('marketData', marketData);
  console.log('goplus (normalized)', goplus);
  console.log('socialPresence', socialPresence);
  
  // Les données sont directement dans metadata (pas dans evaluation_details)
  let antiRug = metadata.anti_rug || {};
  
  // 🆕 Si antiRug est vide, construire depuis les données simples dans metadata
  if (!antiRug.contract_verified && metadata.contract_verified !== undefined) {
    antiRug = {
      contract_verified: { score: metadata.contract_verified || 0, points: metadata.contract_verified || 0, max_points: 20 },
      mint_control: { score: metadata.mint_control || 0, points: metadata.mint_control || 0, max_points: 25 },
      lp_amount: { score: metadata.lp_amount || 0, points: metadata.lp_amount || 0, max_points: 15 },
      lp_locked: { score: metadata.lp_locked || 0, points: metadata.lp_locked || 0, max_points: 15 },
      team_balance: { score: metadata.team_balance || 0, points: metadata.team_balance || 0, max_points: 10 },
      recent_sells: { score: metadata.recent_sells || 0, points: metadata.recent_sells || 0, max_points: 5 },
      tradeability: { score: metadata.tradeability || 0, points: metadata.tradeability || 0, max_points: 10 }
    };
  }
  
  let volumeStrategy = metadata.volume_strategy || {};
  
  // 🆕 Si volumeStrategy est vide, construire depuis moralisDiscovery/metadata
  if (!volumeStrategy.volume_24h && (moralisDiscovery.volume_change_usd || metadata.volume_change_usd)) {
    const volume_24h = (moralisDiscovery.volume_change_usd && moralisDiscovery.volume_change_usd['1d']) ||
                       (metadata.volume_change_usd && metadata.volume_change_usd['1d']) || 0;
    const volume_1h = (moralisDiscovery.volume_change_usd && moralisDiscovery.volume_change_usd['1h']) ||
                      (metadata.volume_change_usd && metadata.volume_change_usd['1h']) || 0;
    volumeStrategy = {
      volume_24h,
      volume_1h,
      classification: volume_24h > 1000000 ? 'high' : volume_24h > 100000 ? 'medium' : 'low'
    };
  }
  
  // Les données brutes sont dans raw_evaluation_details
  const rawDetails = metadata.raw_evaluation_details || {};
  const ultraEarly = rawDetails.ultra_early || {};
  const tokenInfoRaw = rawDetails.token_info || {};
  
  // 🆕 Fusion des données : priorité moralisDiscovery > metadata direct > tokenInfoRaw
  const tokenInfo = {
    price_usd: moralisDiscovery.price_usd || metadata.price_usd || tokenInfoRaw.price_usd,
    market_cap: moralisDiscovery.market_cap || metadata.market_cap || tokenInfoRaw.market_cap,
    volume_24h: (moralisDiscovery.volume_change_usd && moralisDiscovery.volume_change_usd['1d']) || 
                (metadata.volume_change_usd && metadata.volume_change_usd['1d']) || 
                tokenInfoRaw.volume_24h,
    volume_1h: (moralisDiscovery.volume_change_usd && moralisDiscovery.volume_change_usd['1h']) || 
               (metadata.volume_change_usd && metadata.volume_change_usd['1h']) || 
               tokenInfoRaw.volume_1h,
    liquidity_usd: marketData.liquidity_usd || tokenInfoRaw.liquidity_usd,
    price_change_24h: (moralisDiscovery.price_percent_change_usd && moralisDiscovery.price_percent_change_usd['1d']) ||
                      (metadata.price_percent_change_usd && metadata.price_percent_change_usd['1d']) ||
                      tokenInfoRaw.price_change_24h,
    holder_count: goplus.holder_count || tokenInfoRaw.holder_count,
    age_minutes: tokenInfoRaw.age_minutes,
    team_balance_percent: tokenInfoRaw.team_balance_percent,
    transaction_count: tokenInfoRaw.transaction_count
  };
  
  // Social info peut être dans social_presence.social_info
  const social = socialPresence.social_info || {};
  
  // 🔄 Rétrocompatibilité: Données de sécurité (nouveau: metadata.xxx, ancien: rawDetails.xxx)
  const tokensniffer = metadata.tokensniffer_scan || rawDetails.tokensniffer_scan || {};
  const honeypotIs = metadata.honeypot_is_check || rawDetails.honeypot_is_check || {};
  const securityConsensus = metadata.security_consensus || rawDetails.security_consensus || {};
  
  // 🆕 Données Moralis - Support des deux structures (raw_evaluation_details ET directe)
  const moralisHolders = rawDetails.moralis_holders || metadata.moralis_holders || {};
  const moralisPairs = rawDetails.moralis_pairs || metadata.moralis_pairs || {};
  const moralisStats = rawDetails.moralis_stats || metadata.moralis_stats || {};
  const moralisSocial = rawDetails.moralis_social || metadata.moralis_social || {};

  console.log('moralisHolders', moralisHolders);
  console.log('moralisPairs', moralisPairs);
  console.log('moralisStats', moralisStats);
  console.log('moralisSocial', moralisSocial);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <h2>{token.name || 'Sans nom'}</h2>
            <p className="modal-symbol">{token.symbol || 'N/A'}</p>
          </div>
          <button className="modal-close" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="modal-body">
          {/* Informations de Base */}
          <section className="modal-section">
            <h3>📋 Informations de Base</h3>
            <div className="info-grid">
              <div className="info-item info-item-with-copy">
                <span className="info-label">Adresse</span>
                <div className="address-container">
                  <span className="info-value monospace">{token.address}</span>
                  <CopyBtn text={token.address} />
                  <button
                    className={`copy-btn ${copied ? 'copied' : ''}`}
                    onClick={copyAddress}
                    title="Copier l'adresse"
                  >
                    {copied ? '✓' : '📋'}
                  </button>
                </div>
              </div>
              <div className="info-item">
                <span className="info-label">Chaîne</span>
                <span className="info-value">{token.chain.toUpperCase()}</span>
              </div>
              {token.source && (
                <div className="info-item">
                  <span className="info-label">Source</span>
                  <span className="info-value">{formatSource(token.source)}</span>
                </div>
              )}
              {getDetectionRequest() && (
                <div className="info-item info-item-with-copy">
                  <span className="info-label">URL de Détection</span>
                  <div className="address-container">
                    {(() => {
                      const url = getDetectionRequest();
                      const isLink = typeof url === 'string' && (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('wss://'));
                      return isLink ? (
                        <a href={url} target="_blank" rel="noopener noreferrer" className="info-value monospace">
                          {redactUrl(url)}
                        </a>
                      ) : (
                        <span className="info-value monospace">{redactUrl(url)}</span>
                      );
                    })()}
                  </div>
                </div>
              )}
              {(() => {
                const params = getDetectionParams();
                const formattedJson = params ? formatDetectionParamsAsJson(params) : null;
                return formattedJson && formattedJson.length > 0 ? (
                  <div className="info-item" style={{ gridColumn: '1 / -1' }}>
                    <span className="info-label">🔧 Paramètres de Détection</span>
                    <textarea
                      readOnly
                      value={formattedJson}
                      style={{
                        width: '100%',
                        minHeight: '120px',
                        maxHeight: '300px',
                        padding: '8px',
                        fontSize: '0.85em',
                        fontFamily: 'monospace',
                        backgroundColor: 'var(--bg-surface)',
                        border: '1px solid var(--border-primary)',
                        borderRadius: '4px',
                        resize: 'vertical',
                        overflow: 'auto',
                        whiteSpace: 'pre',
                        wordWrap: 'normal',
                        marginTop: '4px'
                      }}
                      onClick={(e) => e.target.select()}
                    />
                  </div>
                ) : null;
              })()}
              <div className="info-item">
                <span className="info-label">Decimals</span>
                <span className="info-value">{token.decimals}</span>
              </div>
              {(tokenInfo.age_minutes != null && calculateTokenCreationDate(token.created_at, tokenInfo.age_minutes)) && (
                <div className="info-item">
                  <span className="info-label">🎂 Créé sur Blockchain</span>
                  <span className="info-value">
                    {calculateTokenCreationDate(token.created_at, tokenInfo.age_minutes)}
                  </span>
                </div>
              )}
              <div className="info-item">
                <span className="info-label">🔍 Détecté par le Bot</span>
                <span className="info-value">{formatDate(token.created_at)}</span>
              </div>
              {tokenInfo.age_minutes != null && (
                <div className="info-item">
                  <span className="info-label">⏱️ Âge au moment de la détection</span>
                  <span className="info-value">
                    {tokenInfo.age_minutes < 60 
                      ? `${Math.floor(tokenInfo.age_minutes)} minutes`
                      : tokenInfo.age_minutes < 1440
                      ? `${Math.floor(tokenInfo.age_minutes / 60)} heures ${Math.floor(tokenInfo.age_minutes % 60)} minutes`
                      : `${Math.floor(tokenInfo.age_minutes / 60 / 24)} jours ${Math.floor((tokenInfo.age_minutes / 60) % 24)} heures`
                    }
                  </span>
                </div>
              )}
            </div>
            <a
              href={getDexScreenerUrl()}
              target="_blank"
              rel="noopener noreferrer"
              className="dexscreener-link"
            >
              🔗 Voir sur DexScreener
            </a>
          </section>

          {/* Scores / Évaluation */}
          {(metadata.score !== undefined || metadata.recommendation || metadata.rejection_reason) && (
            <section className="modal-section">
              <h3>📊 Évaluation</h3>
              <div className="score-big">
                <div className="score-value-big">{metadata.score}</div>
                <div className="score-label-big">/100</div>
              </div>
              <div className="recommendation-badge">
                {metadata.recommendation || 'N/A'}
              </div>
              {/* Afficher la raison du rejet si présente */}
              {metadata.rejection_reason && (
                <div className="rejection-reason-box">
                  <span className="rejection-reason-icon">🚫</span>
                  <span className="rejection-reason-text">{metadata.rejection_reason}</span>
                </div>
              )}
              <div className="info-grid">
                <div className="info-item">
                  <span className="info-label">Contract Verified</span>
                  <span className="info-value">
                    {antiRug.contract_verified?.score || 0}/20
                    {' → '}
                    {antiRug.contract_verified?.points?.toFixed(1) || '0.0'}/{antiRug.contract_verified?.max_points || 20} pts
                    {antiRug.contract_verified?.score >= 15 ? ' ✅' : ' ❌'}
                  </span>
                </div>
                <div className="info-item">
                  <span className="info-label">Mint Control</span>
                  <span className="info-value">
                    {antiRug.mint_control?.score || 0}/25
                    {' → '}
                    {antiRug.mint_control?.points?.toFixed(1) || '0.0'}/{antiRug.mint_control?.max_points || 25} pts
                    {antiRug.mint_control?.score >= 20 ? ' ✅' : ' ❌'}
                  </span>
                </div>
                <div className="info-item" style={{gridColumn: '1 / -1'}}>
                  <span className="info-label">LP Amount</span>
                  <span className="info-value">
                    {antiRug.lp_amount?.score || 0}/15
                    {' → '}
                    {antiRug.lp_amount?.points?.toFixed(1) || '0.0'}/{antiRug.lp_amount?.max_points || 15} pts
                    {antiRug.lp_amount?.score >= 10 ? ' ✅' : ' ❌'}
                  </span>
                  {antiRug.lp_amount?.lp_usd != null && (
                    <div style={{fontSize: '0.9em', color: 'var(--text-muted)', marginTop: '4px'}}>
                      💰 LP: ${antiRug.lp_amount.lp_usd.toLocaleString()} USD
                      {antiRug.lp_amount.lp_address && ` • Adresse: ${antiRug.lp_amount.lp_address.substring(0, 10)}...`}
                    </div>
                  )}
                </div>
                <div className="info-item" style={{gridColumn: '1 / -1'}}>
                  <span className="info-label">LP Locked</span>
                  <span className="info-value">
                    {antiRug.lp_locked?.score || 0}/15
                    {' → '}
                    {antiRug.lp_locked?.points?.toFixed(1) || '0.0'}/{antiRug.lp_locked?.max_points || 15} pts
                    {antiRug.lp_locked?.score >= 10 ? ' ✅' : ' ❌'}
                  </span>
                  {antiRug.lp_locked?.lock_days != null && (
                    <div style={{fontSize: '0.9em', color: 'var(--text-muted)', marginTop: '4px'}}>
                      🔒 Lock: {antiRug.lp_locked.lock_days} jours
                    </div>
                  )}
                </div>
                <div className="info-item" style={{gridColumn: '1 / -1'}}>
                  <span className="info-label">Team Balance</span>
                  <span className="info-value">
                    {antiRug.team_balance?.score || 0}/10
                    {' → '}
                    {antiRug.team_balance?.points?.toFixed(1) || '0.0'}/{antiRug.team_balance?.max_points || 10} pts
                    {antiRug.team_balance?.score >= 8 ? ' ✅' : ' ❌'}
                  </span>
                  {antiRug.team_balance?.team_percent != null && (
                    <div style={{fontSize: '0.9em', color: 'var(--text-muted)', marginTop: '4px'}}>
                      👥 Team détient: {antiRug.team_balance.team_percent.toFixed(1)}% du supply
                    </div>
                  )}
                </div>
                <div className="info-item">
                  <span className="info-label">Recent Sells</span>
                  <span className="info-value">
                    {antiRug.recent_sells?.score || 0}/5
                    {' → '}
                    {antiRug.recent_sells?.points?.toFixed(1) || '0.0'}/{antiRug.recent_sells?.max_points || 5} pts
                    {antiRug.recent_sells?.score >= 3 ? ' ✅' : ' ❌'}
                  </span>
                </div>
                <div className="info-item" style={{gridColumn: '1 / -1'}}>
                  <span className="info-label">Tradeability</span>
                  <span className="info-value">
                    {antiRug.tradeability?.score || 0}/10
                    {' → '}
                    {antiRug.tradeability?.points?.toFixed(1) || '0.0'}/{antiRug.tradeability?.max_points || 10} pts
                    {antiRug.tradeability?.score >= 8 ? ' ✅' : ' ❌'}
                  </span>
                  {antiRug.tradeability?.is_honeypot && (
                    <div style={{fontSize: '0.9em', color: 'var(--color-danger)', marginTop: '4px', fontWeight: 'bold'}}>
                      🍯 HONEYPOT DÉTECTÉ!
                      {antiRug.tradeability.can_trade === false && ' • Cannot Trade'}
                    </div>
                  )}
                </div>
              </div>
            </section>
          )}

          {/* Market Data */}
          {(tokenInfo.price_usd != null || tokenInfo.market_cap != null || tokenInfo.volume_24h != null || tokenInfo.liquidity_usd != null) && (
            <section className="modal-section">
              <h3>💰 Market Data</h3>
              <div className="info-grid">
                {tokenInfo.price_usd != null && (
                  <div className="info-item">
                    <span className="info-label">Prix</span>
                    <span className="info-value">${tokenInfo.price_usd}</span>
                  </div>
                )}
                {tokenInfo.market_cap != null && (
                  <div className="info-item">
                    <span className="info-label">Market Cap</span>
                    <span className="info-value">${tokenInfo.market_cap.toLocaleString()}</span>
                  </div>
                )}
                {tokenInfo.volume_24h != null && (
                  <div className="info-item">
                    <span className="info-label">Volume 24h</span>
                    <span className="info-value">${tokenInfo.volume_24h.toLocaleString()}</span>
                  </div>
                )}
                {tokenInfo.liquidity_usd != null && (
                  <div className="info-item">
                    <span className="info-label">Liquidité</span>
                    <span className="info-value">${tokenInfo.liquidity_usd.toLocaleString()}</span>
                  </div>
                )}
                {tokenInfo.txns_24h_total != null && tokenInfo.txns_24h_total > 0 && (
                  <div className="info-item" style={{gridColumn: '1 / -1'}}>
                    <span className="info-label">Transactions 24h</span>
                    <span className="info-value">
                      {tokenInfo.txns_24h_total.toLocaleString()} transactions
                    </span>
                    {tokenInfo.txns_24h_buys != null && tokenInfo.txns_24h_sells != null && (
                      <div style={{fontSize: '0.9em', color: 'var(--text-muted)', marginTop: '4px'}}>
                        📈 {tokenInfo.txns_24h_buys.toLocaleString()} buys ({((tokenInfo.txns_24h_buys / tokenInfo.txns_24h_total) * 100).toFixed(1)}%)
                        {' • '}
                        📉 {tokenInfo.txns_24h_sells.toLocaleString()} sells ({((tokenInfo.txns_24h_sells / tokenInfo.txns_24h_total) * 100).toFixed(1)}%)
                        {tokenInfo.txns_24h_buys > tokenInfo.txns_24h_sells && (
                          <span style={{color: 'var(--color-success)', fontWeight: 'bold'}}> • 🟢 Pression d'achat</span>
                        )}
                        {tokenInfo.txns_24h_sells > tokenInfo.txns_24h_buys && (
                          <span style={{color: 'var(--color-danger)', fontWeight: 'bold'}}> • 🔴 Pression de vente</span>
                        )}
                      </div>
                    )}
                  </div>
                )}
                {tokenInfo.transaction_count != null && tokenInfo.transaction_count > 0 && !tokenInfo.txns_24h_total && (
                  <div className="info-item">
                    <span className="info-label">Total Transactions</span>
                    <span className="info-value">
                      {tokenInfo.transaction_count.toLocaleString()}
                    </span>
                  </div>
                )}
              </div>
            </section>
          )}

          {/* Présence Sociale */}
          {(social.has_twitter || social.has_website || social.has_telegram) && (
            <section className="modal-section">
              <h3>🌐 Présence Sociale</h3>
              <div className="social-links">
                {social.has_twitter && (
                  <a href={social.twitter_url} target="_blank" rel="noopener noreferrer" className="social-link">
                    🐦 Twitter
                  </a>
                )}
                {social.has_website && (
                  <a href={social.website_url} target="_blank" rel="noopener noreferrer" className="social-link">
                    🌐 Website
                  </a>
                )}
                {social.has_telegram && (
                  <a href={social.telegram_url} target="_blank" rel="noopener noreferrer" className="social-link">
                    📱 Telegram
                  </a>
                )}
              </div>
            </section>
          )}

          {/* Anti-Rug Score */}
          {(ultraEarly.score !== undefined) && (
            <section className="modal-section">
              <h3>🛡️ Anti-Rug Score</h3>
              <div className="info-grid">
                <div className="info-item">
                  <span className="info-label">Score</span>
                  <span className="info-value">{ultraEarly.score}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Passé</span>
                  <span className="info-value">{ultraEarly.passed ? '✅ Oui' : '❌ Non'}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Recommandation</span>
                  <span className="info-value">{ultraEarly.recommendation}</span>
                </div>
              </div>
              {ultraEarly.hard_blocks && ultraEarly.hard_blocks.length > 0 && (
                <div className="hard-blocks">
                  <strong>⚠️ Hard Blocks:</strong>
                  {ultraEarly.hard_blocks.map((block, idx) => (
                    <span key={idx} className="hard-block-badge">
                      {block}
                    </span>
                  ))}
                </div>
              )}
            </section>
          )}

          {/* Volume Strategy */}
          {volumeStrategy && (
            <section className="modal-section">
              <h3>📈 Volume Strategy</h3>
              <div className="info-grid">
                <div className="info-item">
                  <span className="info-label">Volume 24h</span>
                  <span className="info-value">${volumeStrategy.volume_24h?.toLocaleString() || 0}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Volume 1h</span>
                  <span className="info-value">${volumeStrategy.volume_1h?.toLocaleString() || 0}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Classification</span>
                  <span className="info-value">
                    {volumeStrategy.is_high_volume && '🟢 High Volume'}
                    {volumeStrategy.is_medium_volume && '🟡 Medium Volume'}
                    {volumeStrategy.is_low_volume && '🔴 Low Volume'}
                    {!volumeStrategy.is_high_volume && !volumeStrategy.is_medium_volume && !volumeStrategy.is_low_volume && '⚪ Unknown'}
                  </span>
                </div>
              </div>
            </section>
          )}

          {/* GoPlus Security - Données complètes depuis metadata.goplus_security */}
          {goplus && Object.keys(goplus).length > 0 && (
            <section className="modal-section">
              <h3>🔒 GoPlus Security</h3>
              <div className="info-grid">
                <div className="info-item">
                  <span className="info-label">Risk Level</span>
                  <span className="info-value">{goplus.risk_level || 'N/A'}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Honeypot</span>
                  <span className="info-value">{goplus.is_honeypot ? '⚠️ Oui' : '✅ Non'}</span>
                </div>
                {token.is_rug !== null && token.is_rug !== undefined && (
                  <div className="info-item">
                    <span className="info-label">Rug Pull</span>
                    <span className="info-value">{token.is_rug ? '🚨 Oui' : '✅ Non'}</span>
                  </div>
                )}
                <div className="info-item">
                  <span className="info-label">Mintable</span>
                  <span className="info-value">{goplus.is_mintable ? '⚠️ Oui' : '✅ Non'}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Open Source</span>
                  <span className="info-value">{goplus.is_open_source ? '✅ Oui' : '❌ Non'}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Holders</span>
                  <span className="info-value">{goplus.holder_count?.toLocaleString() || 'N/A'}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Confidence</span>
                  <span className="info-value">{goplus.confidence_score || 0}%</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Buy Tax</span>
                  <span className="info-value">{((goplus.buy_tax || 0) * 100).toFixed(1)}%</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Sell Tax</span>
                  <span className="info-value">{((goplus.sell_tax || 0) * 100).toFixed(1)}%</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Can Buy</span>
                  <span className="info-value">{goplus.cannot_buy ? '❌ Non' : '✅ Oui'}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Can Sell All</span>
                  <span className="info-value">{goplus.cannot_sell_all ? '❌ Non' : '✅ Oui'}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Total Liquidity</span>
                  <span className="info-value">${goplus.total_liquidity?.toLocaleString() || 0}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Creator Balance</span>
                  <span className="info-value">
                    {tokenInfo.team_balance_percent != null 
                      ? `${tokenInfo.team_balance_percent.toFixed(1)}%`
                      : ((goplus.creator_percent || 0) * 100).toFixed(1) + '%'
                    }
                  </span>
                </div>
              </div>
            </section>
          )}

          {/* 🆕 TokenSniffer Security */}
          {tokensniffer && Object.keys(tokensniffer).length > 0 && (
            <section className="modal-section">
              <h3>🔍 TokenSniffer Security</h3>
              <div className="info-grid">
                {tokensniffer.score !== undefined && (
                  <div className="info-item">
                    <span className="info-label">Score</span>
                    <span className="info-value">{tokensniffer.score}/100</span>
                  </div>
                )}
                {tokensniffer.is_honeypot !== undefined && (
                  <div className="info-item">
                    <span className="info-label">Honeypot</span>
                    <span className="info-value">{tokensniffer.is_honeypot ? '⚠️ Oui' : '✅ Non'}</span>
                  </div>
                )}
                {tokensniffer.is_scam !== undefined && (
                  <div className="info-item">
                    <span className="info-label">Scam</span>
                    <span className="info-value">{tokensniffer.is_scam ? '⚠️ Oui' : '✅ Non'}</span>
                  </div>
                )}
                {tokensniffer.is_suspicious !== undefined && (
                  <div className="info-item">
                    <span className="info-label">Suspicious</span>
                    <span className="info-value">{tokensniffer.is_suspicious ? '⚠️ Oui' : '✅ Non'}</span>
                  </div>
                )}
                {tokensniffer.contract_verified !== undefined && (
                  <div className="info-item">
                    <span className="info-label">Contract Verified</span>
                    <span className="info-value">{tokensniffer.contract_verified ? '✅ Oui' : '❌ Non'}</span>
                  </div>
                )}
                {tokensniffer.exploits && tokensniffer.exploits.length > 0 && (
                  <div className="info-item" style={{gridColumn: '1 / -1'}}>
                    <span className="info-label">Exploits Détectés</span>
                    <div style={{fontSize: '0.9em', color: 'var(--color-danger)', marginTop: '4px'}}>
                      {tokensniffer.exploits.map((exploit, idx) => (
                        <div key={idx}>⚠️ {exploit}</div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </section>
          )}

          {/* 🆕 Honeypot.is Security */}
          {honeypotIs && Object.keys(honeypotIs).length > 0 && (
            <section className="modal-section">
              <h3>🍯 Honeypot.is Security</h3>
              <div className="info-grid">
                {honeypotIs.is_honeypot !== undefined && (
                  <div className="info-item">
                    <span className="info-label">Honeypot</span>
                    <span className="info-value">{honeypotIs.is_honeypot ? '⚠️ Oui' : '✅ Non'}</span>
                  </div>
                )}
                {honeypotIs.risk_level && (
                  <div className="info-item">
                    <span className="info-label">Risk Level</span>
                    <span className="info-value">
                      {honeypotIs.risk_level === 'critical' && '🔴 Critical'}
                      {honeypotIs.risk_level === 'high' && '🟠 High'}
                      {honeypotIs.risk_level === 'medium' && '🟡 Medium'}
                      {honeypotIs.risk_level === 'low' && '🟢 Low'}
                      {honeypotIs.risk_level === 'safe' && '✅ Safe'}
                    </span>
                  </div>
                )}
                {honeypotIs.buy_tax !== undefined && (
                  <div className="info-item">
                    <span className="info-label">Buy Tax</span>
                    <span className="info-value">{(honeypotIs.buy_tax * 100).toFixed(1)}%</span>
                  </div>
                )}
                {honeypotIs.sell_tax !== undefined && (
                  <div className="info-item">
                    <span className="info-label">Sell Tax</span>
                    <span className="info-value">{(honeypotIs.sell_tax * 100).toFixed(1)}%</span>
                  </div>
                )}
                {honeypotIs.transfer_tax !== undefined && (
                  <div className="info-item">
                    <span className="info-label">Transfer Tax</span>
                    <span className="info-value">{(honeypotIs.transfer_tax * 100).toFixed(1)}%</span>
                  </div>
                )}
                {honeypotIs.simulation_success !== undefined && (
                  <div className="info-item">
                    <span className="info-label">Simulation Success</span>
                    <span className="info-value">{honeypotIs.simulation_success ? '✅ Oui' : '❌ Non'}</span>
                  </div>
                )}
                {honeypotIs.issues && honeypotIs.issues.length > 0 && (
                  <div className="info-item" style={{gridColumn: '1 / -1'}}>
                    <span className="info-label">Issues Détectés</span>
                    <div style={{fontSize: '0.9em', color: 'var(--color-danger)', marginTop: '4px'}}>
                      {honeypotIs.issues.map((issue, idx) => (
                        <div key={idx}>⚠️ {issue}</div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </section>
          )}

          {/* 🆕 Security Consensus */}
          {securityConsensus && Object.keys(securityConsensus).length > 0 && (
            <section className="modal-section">
              <h3>🎯 Security Consensus (3 Providers)</h3>
              <div style={{fontSize: '0.9em', color: 'var(--text-muted)', marginBottom: '12px', padding: '8px', backgroundColor: 'var(--bg-surface)', borderRadius: '6px'}}>
                <strong>ℹ️ Comment ça marche :</strong> 3 providers (GoPlus, TokenSniffer, Honeypot.is) analysent le token. 
                Plus il y a de providers disponibles (3/3), plus le consensus est fiable. 
                <strong> 0 vote = ✅ Sûr</strong> · <strong>1+ votes = ⚠️ Danger</strong>
              </div>
              <div className="info-grid">
                <div className="info-item">
                  <span className="info-label">Votes Honeypot</span>
                  <span className="info-value">
                    {securityConsensus.honeypot_votes || 0}/3
                    {securityConsensus.is_honeypot && ' 🚨'}
                  </span>
                  {securityConsensus.voters && securityConsensus.voters.honeypot && securityConsensus.voters.honeypot.length > 0 && (
                    <div style={{fontSize: '0.85em', color: 'var(--text-muted)', marginTop: '4px'}}>
                      {securityConsensus.voters.honeypot.join(', ')}
                    </div>
                  )}
                </div>
                <div className="info-item">
                  <span className="info-label">Votes Scam</span>
                  <span className="info-value">
                    {securityConsensus.scam_votes || 0}/3
                    {securityConsensus.is_scam && ' ⚠️'}
                  </span>
                  {securityConsensus.voters && securityConsensus.voters.scam && securityConsensus.voters.scam.length > 0 && (
                    <div style={{fontSize: '0.85em', color: 'var(--text-muted)', marginTop: '4px'}}>
                      {securityConsensus.voters.scam.join(', ')}
                    </div>
                  )}
                </div>
                <div className="info-item">
                  <span className="info-label">Votes Suspicious</span>
                  <span className="info-value">
                    {securityConsensus.suspicious_votes || 0}/3
                  </span>
                  {securityConsensus.voters && securityConsensus.voters.suspicious && securityConsensus.voters.suspicious.length > 0 && (
                    <div style={{fontSize: '0.85em', color: 'var(--text-muted)', marginTop: '4px'}}>
                      {securityConsensus.voters.suspicious.join(', ')}
                    </div>
                  )}
                </div>
                <div className="info-item">
                  <span className="info-label">Confiance</span>
                  <span className="info-value">
                    {securityConsensus.confidence === 'high' && '🟢 Haute (3/3)'}
                    {securityConsensus.confidence === 'medium' && '🟡 Moyenne (2/3)'}
                    {securityConsensus.confidence === 'low' && '🟠 Faible (1/3)'}
                    {(!securityConsensus.confidence || securityConsensus.confidence === 'unknown') && '⚪ Inconnue (0/3)'}
                  </span>
                </div>
              </div>
            </section>
          )}

          {/* 🆕 Moralis Holders Data */}
          {moralisHolders && Object.keys(moralisHolders).length > 0 && (
            <section className="modal-section">
              <h3>👥 Moralis Holders Analysis</h3>
              <div className="info-grid">
                {moralisHolders.total_holder_count !== undefined && (
                  <div className="info-item">
                    <span className="info-label">Total Holders</span>
                    <span className="info-value">{moralisHolders.total_holder_count.toLocaleString()}</span>
                  </div>
                )}
                {moralisHolders.top_holder_percent !== undefined && (
                  <div className="info-item">
                    <span className="info-label">Top Holder</span>
                    <span className="info-value">{moralisHolders.top_holder_percent.toFixed(2)}%</span>
                  </div>
                )}
                {moralisHolders.top_10_percent !== undefined && (
                  <div className="info-item">
                    <span className="info-label">Top 10 Holders</span>
                    <span className="info-value">{moralisHolders.top_10_percent.toFixed(2)}%</span>
                  </div>
                )}
                {moralisHolders.top_holder_address && (
                  <div className="info-item" style={{gridColumn: '1 / -1'}}>
                    <span className="info-label">Top Holder Address</span>
                    <span className="info-value monospace" style={{fontSize: '0.85em'}}>
                      {moralisHolders.top_holder_address}
                    </span>
                  </div>
                )}
              </div>
            </section>
          )}

          {/* 🆕 Moralis Pairs Data */}
          {moralisPairs && Object.keys(moralisPairs).length > 0 && (
            <section className="modal-section">
              <h3>💧 Moralis Pairs & Liquidity</h3>
              <div className="info-grid">
                {moralisPairs.pair_count !== undefined && (
                  <div className="info-item">
                    <span className="info-label">Nombre de Pairs</span>
                    <span className="info-value">{moralisPairs.pair_count}</span>
                  </div>
                )}
                {moralisPairs.total_liquidity_usd !== undefined && (
                  <div className="info-item">
                    <span className="info-label">Liquidité Totale</span>
                    <span className="info-value">${moralisPairs.total_liquidity_usd.toLocaleString()}</span>
                  </div>
                )}
                {moralisPairs.total_volume_24h_usd !== undefined && (
                  <div className="info-item">
                    <span className="info-label">Volume 24h</span>
                    <span className="info-value">${moralisPairs.total_volume_24h_usd.toLocaleString()}</span>
                  </div>
                )}
                {moralisPairs.main_dex && (
                  <div className="info-item">
                    <span className="info-label">DEX Principal</span>
                    <span className="info-value">{moralisPairs.main_dex}</span>
                  </div>
                )}
              </div>
            </section>
          )}

          {/* 🆕 Moralis Stats Data */}
          {moralisStats && Object.keys(moralisStats).length > 0 && !moralisStats.premium_required && (
            <section className="modal-section">
              <h3>📊 Moralis Stats (Momentum)</h3>
              <div className="info-grid">
                {moralisStats.holder_growth_24h !== undefined && (
                  <div className="info-item">
                    <span className="info-label">Croissance Holders 24h</span>
                    <span className="info-value" style={{color: moralisStats.holder_growth_24h > 0 ? '#48bb78' : '#f56565'}}>
                      {moralisStats.holder_growth_24h > 0 ? '+' : ''}{moralisStats.holder_growth_24h}
                    </span>
                  </div>
                )}
                {moralisStats.smart_buyers_count !== undefined && (
                  <div className="info-item">
                    <span className="info-label">Smart Buyers</span>
                    <span className="info-value">{moralisStats.smart_buyers_count}</span>
                  </div>
                )}
                {moralisStats.liquidity_added_24h_usd !== undefined && (
                  <div className="info-item">
                    <span className="info-label">Liquidité Ajoutée 24h</span>
                    <span className="info-value">${moralisStats.liquidity_added_24h_usd.toLocaleString()}</span>
                  </div>
                )}
                {moralisStats.price_change_24h !== undefined && (
                  <div className="info-item">
                    <span className="info-label">Variation Prix 24h</span>
                    <span className="info-value" style={{color: moralisStats.price_change_24h > 0 ? '#48bb78' : '#f56565'}}>
                      {moralisStats.price_change_24h > 0 ? '+' : ''}{moralisStats.price_change_24h.toFixed(2)}%
                    </span>
                  </div>
                )}
              </div>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}

export default TokenModal;
